import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Pencil, Trash2, Search, Mail, Send, Check } from "lucide-react";
import { formatDMY } from "@/lib/date";
import UserDialog from "@/components/users/UserDialog";
import InviteDialog from "@/components/users/InviteDialog";
import { ROLES } from "@/components/users/shared";

function lastLoginLabel(iso) {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return formatDMY(iso);
  } catch (_) {
    return iso.slice(0, 10);
  }
}

export default function Users() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [photogs, setPhotogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [flash, setFlash] = useState("");
  const [query, setQuery] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setList(data);
      setErr("");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  useEffect(() => {
    load();
    api.get("/photographers").then((r) => setPhotogs(r.data)).catch(() => {});
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (u) =>
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q)
    );
  }, [list, query]);

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (ex) {
      alert(formatApiError(ex.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    if (me?.id === u.id && u.active !== false) {
      alert("You can't deactivate your own account.");
      return;
    }
    const next = u.active === false;
    try {
      await api.patch(`/users/${u.id}`, { active: next });
      load();
    } catch (ex) {
      alert(formatApiError(ex.response?.data?.detail));
    }
  };

  const resendInvite = async (u) => {
    setResendingId(u.id);
    setFlash("");
    try {
      await api.post(`/users/${u.id}/resend-invite`);
      setFlash(`Invitation re-sent to ${u.email}.`);
      setTimeout(() => setFlash(""), 4000);
    } catch (ex) {
      alert(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="ccs-label mb-2">Administration</div>
          <h1 className="text-4xl font-semibold tracking-tight">Users &amp; Roles</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setInviting({ email: "", name: "", role: "VIEWER", photographer_id: "" })}
            className="ccs-btn-ghost"
            data-testid="invite-user-button"
            title="Send an email invitation — the invitee chooses their own password"
          >
            <Mail size={16} /> Invite user
          </button>
          <button
            onClick={() => setEditing({ email: "", name: "", role: "SECRETARY", password: "", active: true })}
            className="ccs-btn"
            data-testid="add-user-button"
          >
            <Plus size={16} /> New user
          </button>
        </div>
      </div>

      {flash && (
        <div className="success-text text-sm mb-3" data-testid="users-flash">{flash}</div>
      )}

      <div className="mb-4 relative max-w-md">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 muted-text pointer-events-none"
        />
        <input
          className="ccs-input pl-9"
          placeholder="Search by name, email or role…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="users-search-input"
        />
      </div>

      {err && <div className="danger-text text-sm mb-3">{err}</div>}

      <div className="ccs-card p-0 overflow-x-auto">
        <table className="ccs-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last login</th>
              <th>Linked</th>
              <th>Created</th>
              <th style={{ width: 140 }}></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => {
              const isSelf = me?.id === u.id;
              const isActive = u.active !== false;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`} data-active={isActive}>
                  <td className="mono">
                    {u.email}
                    {isSelf && <span className="ml-2 tag" style={{ background: "#2a1a05", color: "#fbbf24" }}>you</span>}
                  </td>
                  <td>{u.name}</td>
                  <td><span className="tag">{u.role}</span></td>
                  <td>
                    {u.pending_invite ? (
                      <span
                        className="tag"
                        style={{ background: "#1e1b0d", color: "#fbbf24" }}
                        title="This user has been invited but hasn't accepted yet."
                        data-testid={`user-pending-${u.id}`}
                      >
                        Pending invite
                      </span>
                    ) : isActive ? (
                      <span className="tag" style={{ background: "#0d2b1a", color: "#4ade80" }}>Active</span>
                    ) : (
                      <span className="tag" style={{ background: "#2b0d0d", color: "#f87171" }}>Disabled</span>
                    )}
                    {u.email_verified === false && !u.pending_invite && (
                      <span
                        className="tag ml-1"
                        style={{ background: "#2a1a05", color: "#fbbf24" }}
                        title="This user hasn't clicked the verification link yet."
                        data-testid={`user-unverified-${u.id}`}
                      >
                        Unverified
                      </span>
                    )}
                  </td>
                  <td className="mono muted-text text-xs" data-testid={`user-last-login-${u.id}`}>
                    {lastLoginLabel(u.last_login_at)}
                  </td>
                  <td className="mono muted-text text-xs">
                    {u.role === "MEMBER" && u.photographer_id
                      ? photogs.find((p) => p.id === u.photographer_id)?.photographer_code || "—"
                      : "—"}
                  </td>
                  <td className="mono muted-text text-xs">{u.created_at?.slice(0, 10)}</td>
                  <td className="text-right whitespace-nowrap">
                    {u.pending_invite && (
                      <button
                        onClick={() => resendInvite(u)}
                        disabled={resendingId === u.id}
                        className="p-2 hover:bg-neutral-800 text-amber-400 disabled:opacity-40"
                        title="Re-send invitation email"
                        data-testid={`resend-invite-${u.id}`}
                      >
                        <Send size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => toggleActive(u)}
                      className="p-2 hover:bg-neutral-800"
                      title={isActive ? "Deactivate (block sign-in)" : "Re-activate"}
                      disabled={isSelf && isActive}
                      data-testid={`toggle-active-${u.id}`}
                    >
                      <Check size={14} className={isActive ? "text-emerald-400" : "text-neutral-600"} />
                    </button>
                    <button
                      onClick={() => setEditing({ ...u, password: "" })}
                      className="p-2 hover:bg-neutral-800"
                      title="Edit user"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => remove(u)}
                      disabled={isSelf}
                      title={isSelf ? "You can't delete yourself" : "Delete user"}
                      className="p-2 hover:bg-neutral-800 text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan="8" className="muted-text text-center py-8">
                  {query ? `No users match "${query}".` : "No users yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserDialog
          initial={editing}
          photogs={photogs}
          me={me}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {inviting && (
        <InviteDialog
          initial={inviting}
          photogs={photogs}
          onClose={() => setInviting(null)}
          onSent={(email) => {
            setInviting(null);
            setFlash(`Invitation sent to ${email}. They'll receive an email with a link to choose their password.`);
            setTimeout(() => setFlash(""), 6000);
            load();
          }}
        />
      )}
    </div>
  );
}

